import { BarChart3, Copy, FolderOpen, Plus, RefreshCw, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { ActionInfo, Button, TextField } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import type { TestingAccess, TestingKind, TestProductDraft, TestProductsActions, TestProductsVm } from '../model/types'
import { StateBadge, TestingGate } from './TestingParts'

const EMPTY_DRAFT: TestProductDraft = { name: '', sku: '', barcodes: '' }

/**
 * Тестовые товары замеров. Новый — с нуля или копией товара каталога; копия дальше
 * живёт сама и правится, не трогая каталог. Товары общие у поиска по фото и по тексту:
 * экран один, но поиску по тексту фото не нужны — он их не показывает (RP-FB34).
 */
export function TestProductsScreen({
  access,
  vm,
  actions,
  kind = 'photo',
}: {
  access: TestingAccess
  vm: TestProductsVm
  actions: TestProductsActions
  kind?: TestingKind
}) {
  const photo = kind === 'photo'
  const [draft, setDraft] = useState<TestProductDraft>(EMPTY_DRAFT)
  const [catalogQuery, setCatalogQuery] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const create = () => {
    actions.create(draft)
    setDraft(EMPTY_DRAFT)
  }

  return (
    <TestingGate access={access}>
      <section aria-labelledby="platform-testing-products-title" className="platform-testing">
        <header className="platform-resources__toolbar">
          <h2 id="platform-testing-products-title">{photo ? 'Поиск по фото · Товары' : 'Поиск по тексту · Товары'}</h2>
          <div className="platform-testing__row">
            <Button variant="ghost" icon={<BarChart3 size={16} aria-hidden="true" />} onClick={actions.openRuns} actionId="platform.testing-products.open-runs">
              Замеры
            </Button>
            <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.testing-products.reload">
              Обновить
            </Button>
          </div>
        </header>
        {photo ? (
          <ol className="platform-testing__howto" aria-label="Как пользоваться">
            <li>
              <strong>Добавьте товар</strong> из каталога — его фото из 1С станут эталонами.
            </li>
            <li>
              <strong>Откройте товар</strong> и добавьте 2–3 кадра — снимки, какими их сделает продавец.
            </li>
            <li>
              <strong>Замеры</strong> (вверху справа) прогонят все кадры и покажут, как часто поиск находит нужный товар.
            </li>
          </ol>
        ) : (
          <ol className="platform-testing__howto" aria-label="Как пользоваться">
            <li>
              <strong>Добавьте товар</strong> из каталога — название, артикул и штрихкоды придут сами.
            </li>
            <li>
              <strong>Вернитесь к запросам</strong> («← Назад») и нажмите «Сгенерировать» — для товара появятся запросы.
            </li>
            <li>
              <strong>Замеры</strong> (вверху справа) прогонят все запросы и покажут, как часто поиск находит нужный товар.
            </li>
          </ol>
        )}
        <p className="platform-testing__note">
          Тестовые товары живут отдельно от каталога: боевой поиск их не видит, а замер ищет только среди них.
          {photo ? ' Эти же товары — в поиске по тексту.' : ' Эти же товары — в поиске по фото, там к ним добавляются фото.'}
        </p>
        <ActionError error={vm.action.error} onReload={actions.reload} />

        <section aria-label="Из товара каталога">
          <h3>Добавить товар из каталога</h3>
          <p className="platform-testing__note">
            {photo
              ? 'Так проще всего: название, артикул, штрихкоды и все фото товара из 1С придут сами, фото станут эталонами.'
              : 'Так проще всего: название, артикул и штрихкоды товара придут сами.'}
          </p>
          <form
            className="platform-testing__search"
            onSubmit={(event) => {
              event.preventDefault()
              actions.searchCatalog(catalogQuery.trim())
            }}
          >
            <TextField label="Товар каталога" type="search" placeholder="название, артикул или штрихкод" value={catalogQuery} onChange={setCatalogQuery} />
            <Button type="submit" icon={<Search size={16} aria-hidden="true" />} actionId="platform.testing-products.search-catalog">
              Найти
            </Button>
          </form>
          {vm.catalog.query !== null &&
            (vm.catalog.query.status !== 'ready' ? (
              <QueryState query={vm.catalog.query} emptyText="В каталоге ничего не нашлось" onRetry={() => actions.searchCatalog(catalogQuery.trim())} />
            ) : (
              <ul aria-label="Товары каталога" className="platform-testing__picks">
                {vm.catalog.items.map((item) => (
                  <li key={item.id}>
                    <div className="platform-testing__pick-text">
                      <span>{item.name}</span>
                      {item.sku !== null && <small>Артикул {item.sku}</small>}
                    </div>
                    <Button
                      variant="ghost"
                      icon={<Copy size={16} aria-hidden="true" />}
                      aria-label={`Сделать тестовый товар из «${item.name}»`}
                      disabled={vm.action.busy}
                      onClick={() => actions.copyFromCatalog(item.id)}
                      actionId="platform.testing-products.copy-from-catalog"
                    >
                      Взять в тест
                    </Button>
                  </li>
                ))}
              </ul>
            ))}
        </section>

        <ActionInfo id="platform.testing-products.manual" label="Завести вручную" layout="block">
          <details className="platform-testing__manual">
            <summary>
              {photo ? 'Или завести товар вручную — если его нет в каталоге; фото тогда добавите сами' : 'Или завести товар вручную — если его нет в каталоге'}
            </summary>
            <section aria-label="Новый тестовый товар">
              <div className="platform-testing__fields">
                <TextField label="Название" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
                <TextField label="Артикул" value={draft.sku} onChange={(sku) => setDraft({ ...draft, sku })} />
                <TextField label="Штрихкоды" placeholder="через запятую" value={draft.barcodes} onChange={(barcodes) => setDraft({ ...draft, barcodes })} />
              </div>
              <div className="platform-testing__actions">
                <Button
                  variant="primary"
                  icon={<Plus size={16} aria-hidden="true" />}
                  disabled={draft.name.trim() === '' || vm.action.busy}
                  disabledReason={draft.name.trim() === '' ? 'Нужно название' : null}
                  onClick={create}
                  actionId="platform.testing-products.create"
                >
                  Создать
                </Button>
              </div>
            </section>
          </details>
        </ActionInfo>

        <section aria-label="Тестовые товары">
          <h3>Тестовые товары</h3>
          {vm.query.status !== 'ready' ? (
            <QueryState query={vm.query} emptyText="Тестовых товаров ещё нет" onRetry={actions.reload} />
          ) : (
            <div className="platform-sync__table-wrap">
              <table className="platform-sync__table platform-testing__products">
                <thead>
                  <tr>
                    <th>Товар</th>
                    <th>Штрихкоды</th>
                    {photo && <th>Фото</th>}
                    <th>Индекс</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {row.name}
                        {row.sku === null ? null : <small> · {row.sku}</small>}
                        {row.sourceText === null ? null : <div className="platform-testing__note">{row.sourceText}</div>}
                      </td>
                      <td>{row.barcodes}</td>
                      {photo && <td>{row.photos}</td>}
                      <td>
                        <StateBadge badge={row.index} />
                      </td>
                      <td>
                        <div className="platform-testing__row">
                          <Button
                            variant="ghost"
                            icon={<FolderOpen size={16} aria-hidden="true" />}
                            aria-label={`Открыть ${row.name}`}
                            onClick={() => actions.open(row.id)}
                            actionId="platform.testing-products.open"
                          >
                            Открыть
                          </Button>
                          {confirmDelete === row.id ? (
                            <>
                              <Button
                                variant="danger"
                                icon={<Trash2 size={16} aria-hidden="true" />}
                                onClick={() => {
                                  setConfirmDelete(null)
                                  actions.remove(row.id)
                                }}
                                actionId="platform.testing-products.delete"
                              >
                                Подтвердить удаление
                              </Button>
                              <Button variant="ghost" onClick={() => setConfirmDelete(null)} actionId="platform.testing-products.delete">
                                Не удалять
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              icon={<Trash2 size={16} aria-hidden="true" />}
                              aria-label={`Удалить ${row.name}`}
                              onClick={() => setConfirmDelete(row.id)}
                              actionId="platform.testing-products.delete"
                            >
                              Удалить
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </section>
    </TestingGate>
  )
}
